import { Box, Button, Menu, MenuItem } from "@mui/material";
import { GridEventListener } from "@mui/x-data-grid";
import { useEffect } from "react";
import { generatePath, Outlet, useNavigate } from "react-router-dom";
import { useGetPartnerQuery } from "src/shared/api/partner";
import { TablePage } from "src/shared/components/Table";
import { routes } from "src/shared/constants/routes.constants";
import { useMenu } from "src/shared/hooks";
import { Can } from "src/shared/lib/rback/Can";
import { useBreadcrumbs } from "src/shared/providers/BreadcrumbsProvider";
import { useGetAllContractsList } from "../model/hooks/useGetAllKindsContractsList";
import { useGetTableColumns } from "../model/hooks/useGetTableColumns";
import { IPartnerComplexContractItem } from "../model/types";

interface PartnerContractsListProps {
  partner_id: string;
}
export function PartnerContractsList({
  partner_id,
}: PartnerContractsListProps) {
  const navigate = useNavigate();
  const { anchorEl, open, handleOpen, handleClose } = useMenu();
  const { tableColumns } = useGetTableColumns();
  const { rows, isFetching } = useGetAllContractsList({ partner_id });
  const { data: partner } = useGetPartnerQuery(partner_id);
  const { setBreadcrumbs } = useBreadcrumbs();

  const handleRowClick: GridEventListener<"rowClick"> = ({
    row: { type, id, contract_id },
  }) => {
    if (type === "virtual") {
      navigate(
        generatePath(routes.partnerVirtualContractItem, {
          contract_id: id,
          partner_id,
        }),
      );

      return;
    }
    if (type === "complex") {
      navigate(
        generatePath(routes.partnerComplexContractItem, {
          contract_id: id,
          partner_id,
        }),
      );

      return;
    }
    if (type === "agreement") {
      navigate(
        generatePath(routes.partnerComplexContractAgreement, {
          contract_id: contract_id,
          partner_id,
          agreement_id: id,
        }),
      );
    }
  };

  useEffect(() => {
    setBreadcrumbs([
      { label: "Партнеры", path: routes.partners },
      {
        label: partner?.name || "",
        path: generatePath(routes.partnerItem, { partner_id }),
      },
      {
        label: "Договоры и доп. соглашения",
        path: generatePath(routes.partnerContractsList, { partner_id }),
      },
    ]);

    return () => setBreadcrumbs([]);
  }, [partner_id, setBreadcrumbs, partner?.name]);

  return (
    <>
      <Outlet />
      <TablePage<IPartnerComplexContractItem>
        disableCellSelectionOnClick
        loading={isFetching}
        disableColumnSelector
        onRowClick={handleRowClick}
        unstable_rowSpanning
        getRowId={(row) => String(row.id) + (row.type ?? "")}
        title={
          <Box>
            <Can perform="partners.contract.edit">
              <Button onClick={handleOpen}>Новый договор</Button>
            </Can>
            <Menu
              open={open}
              anchorEl={anchorEl}
              onClick={handleClose}
              onClose={handleClose}
            >
              <MenuItem
                onClick={() =>
                  navigate(
                    generatePath(routes.partnerComplexContractCreate, {
                      partner_id,
                    }),
                  )
                }
              >
                Комплексный
              </MenuItem>
              <MenuItem
                onClick={() =>
                  navigate(
                    generatePath(routes.partnerVirtualContractCreate, {
                      partner_id,
                    }),
                  )
                }
              >
                Виртуальный
              </MenuItem>
            </Menu>
          </Box>
        }
        columns={tableColumns}
        rowCount={rows.length}
        rows={rows}
        hidePagination
      />
    </>
  );
}
